import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import client from "../../lib/api";

// One question before the plan is canceled. The answer is texted to the founder.
export default function CancelDialog({ open, onOpenChange, user, onDone }) {
  const [reasons, setReasons] = useState([]);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setReason(""); setNote("");
    client.get("/billing/cancel/reasons").then((r) => setReasons(r.data.reasons || [])).catch(() => {});
  }, [open]);
  const trialing = user?.subscription_status === "trialing";
  const submit = async () => {
    if (!reason) return toast.error("Pick a reason so we know what to fix");
    setBusy(true);
    try {
      const { data } = await client.post("/billing/cancel", { reason, note });
      const when = data.ends_at ? new Date(data.ends_at).toLocaleDateString() : "the end of your period";
      toast.success(trialing ? `Trial canceled — your card won't be charged. Access runs until ${when}.` : `Plan canceled — access runs until ${when}, no further charges.`);
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't cancel — try Manage billing.");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111] border-white/10 text-white sm:max-w-md" data-testid="cancel-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">One quick question before you go</DialogTitle>
          <DialogDescription className="text-neutral-400">
            {trialing ? "Your card won't be charged. You keep access until the trial ends." : "No further charges. You keep access until the end of what you've paid for."} What made you cancel?
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-2 mt-1" data-testid="cancel-reasons">
          {reasons.map((r) => (
            <button key={r.key} type="button" onClick={() => setReason(r.key)} data-testid={`cancel-reason-${r.key}`}
              className={`text-left rounded-xl border px-4 py-2.5 text-sm transition-colors ${reason === r.key ? "border-[#FF5A1F] bg-[#FF5A1F]/10 text-white" : "border-white/10 text-neutral-300 hover:border-white/25"}`}>
              {r.label}
            </button>
          ))}
        </div>
        <textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 300))} rows={2} placeholder="Anything else? (optional — Mike reads these)"
          className="w-full rounded-xl bg-black/40 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-[#FF5A1F]/60" data-testid="cancel-note" />
        <div className="flex flex-wrap items-center justify-end gap-2 mt-1">
          <button type="button" onClick={() => onOpenChange(false)} className="rounded-full px-4 py-2 text-sm bg-[#FF5A1F] text-black font-semibold hover:bg-[#FF7038]" data-testid="cancel-keep-btn">Keep my plan</button>
          <button type="button" onClick={submit} disabled={busy || !reason} className="rounded-full px-4 py-2 text-sm border border-white/15 text-neutral-300 hover:text-white hover:border-white/30 disabled:opacity-50" data-testid="cancel-confirm-btn">
            {busy ? "Canceling…" : trialing ? "Cancel my trial" : "Cancel my plan"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
